import React, { useEffect, useRef, useState } from 'react';
import {
  Button, Card, Col, Form, FormControl, Row, InputGroup
} from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';
import BackBtn from '../../components/UI/BackBtn';
import Required from '../../components/Required';
import UserGroupEditor from '../../components/common/UserGroupEditor';
import Select from 'react-select';
import api from '../../api';
import useUserContext from '../../hooks/useUserContext';
import { isAdmin } from '../../services/rolesSvc';
import useToast from '../../hooks/useToast';
import moment from 'moment';
import DateControl from '../../components/UI/DateControl';


const getUTCDate = (date) => new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))


function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export default function StratProjectCreate() {
  const query = useQuery();
  const userCtx = useUserContext();
  const addToast = useToast();
  const history = useHistory();
  const [objectOptions, setObjectOptions] = useState([]);
  
  // #region Form validation states
  const [validated, setValidated] = useState(false);
  const formEl = useRef(null);
  // #endregion

  // #region Data states
  const [newObject, setNewObject] = useState({
    id: query.get('id') || 0,
    name: '',
    users: [],
  });
  const [roles, setRoles] = useState([]);
  // #endregion

  // #region Get data
  useEffect(() => {
    const fetchData = async () => {
      const tagsResponse = await api.get('/mng/strategies/types');
      const mappedTags = tagsResponse.map((item) => ({
        value: item.code,
        label: item.name,
        prefix: item.prefix,
        isDisabled: item.code === 'strategy',
      }));
      setObjectOptions(mappedTags);
  
      await getStrategy(query.get('id') || newObject.id);
    };
  
    fetchData();
  }, []);

  const getStrategy = async (id) => {
    if (id) {
      const [resStrat, resRoles] = await Promise.all([
        api.get(`/mng/strategies/${id}`),
        api.get('/mng/roles/strategies/users'),
      ]);
      setNewObject(resStrat);
      setRoles(resRoles);
    } else {
      const resRoles = await api.get('/mng/roles/strategies/users');
      setRoles(resRoles);
    }
  };
  // #endregion

  // #region Save
  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      const invalidEl = formEl.current.querySelector(':invalid');
      invalidEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
    } else {
      await save();
    }
    setValidated(true);
  };

  const save = async () => {
    if(newObject.users.some(user => !user.sAMAccountName)){
      addToast.error('Необходимо заполнить все обязательные поля у пользователя');
      return;
    }
    if (isAdmin(userCtx)) {
      const res = await api.post(`/mng/strategies/${newObject.id}`, newObject);
      if (res.id) {
        addToast.success('Изменения сохранены');
        await getStrategy(res.id);
      } else {
        addToast.error(res.error && res.error.message || 'Не удалось сохранить');
      }
    }
  };
  // #endregion

  // #region Change handlers

  const handleChange = (field, value) => {
    setNewObject(prevState => ({...prevState, [field]: value}))
  }
  // #endregion

  return (
    <section className="page form-page project-card-page">
      <div className="container">
        <div style={{ marginBottom: 16 }}>
          <BackBtn />
        </div>
        <Form noValidate validated={validated} onSubmit={handleSubmit} ref={formEl}>
          <Card className="mb-4">
            <Card.Header as={Card.Title} className="p-4">Информация об объекте</Card.Header>
            <Card.Body className="p-4">
              <Row className='mb-3'>
                <Form.Group as={Col} md="4">
                  <Form.Label>
                    Выбор объекта
                    <Required />
                  </Form.Label>
                  <Select
                    placeholder='Выберите из списка'
                    options={objectOptions}
                    value={objectOptions.find(opt => opt.value === newObject.type)}
                    onChange={(opt) => handleChange('type', opt.value)}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({...base, zIndex: 11}),
                      control: (base) => validated && !newObject.type ? ({...base, border: '1px solid #dc3545'}) : base,
                      indicatorSeparator: (base) => validated && !newObject.type ? ({...base, backgroundColor: '#dc3545'}) : base
                    }}
                  />
                  {validated && !newObject.type && <p style={{color: '#dc3545', fontSize: '.875rem'}}>Обязательное поле к заполнению</p>}
                </Form.Group>
                <Form.Group as={Col} md="8">
                  <Form.Label>
                    Наименование объекта
                    <Required />
                  </Form.Label>
                  <Form.Control
                    required
                    placeholder="Введите наименование объекта"
                    type="text"
                    value={newObject.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                  />
                  <Form.Control.Feedback type="invalid">Обязательное поле к заполнению</Form.Control.Feedback>
                </Form.Group>
              </Row>
              <Row className="mb-4">
                <Form.Group as={Col} md="4">
                  <Form.Label>
                    Номер объекта
                    <Required />
                  </Form.Label>
                  <InputGroup className="mb-3">
                    {newObject.type && <InputGroup.Text id="strat-num-addon">{objectOptions.find(opt => opt.value === newObject.type).prefix}</InputGroup.Text>}
                    <FormControl
                      type='number'
                      disabled={!newObject.type || !!newObject.id}
                      required
                      placeholder="Введите номер объекта"
                      aria-label="Введите номер объекта"
                      value={newObject.intNum}
                      onChange={(e) => handleChange('intNum', parseInt(e.target.value))}
                    />
                  <Form.Control.Feedback type="invalid">Обязательное поле к заполнению</Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                <Form.Group as={Col} md="3">
                  <Form.Label>
                    Дата окончания
                  </Form.Label>
                    <DateControl
                      disabled={!newObject.isArchived}
                      placeholder={'дд.мм.гггг'}
                      value={newObject.endDate ? moment(newObject.endDate).format('DD.MM.YYYY') : ''}
                      onChange={(date) => handleChange('endDate', getUTCDate(date).toISOString())}
                    />
                  <Form.Control.Feedback type="invalid">Обязательное поле к заполнению</Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} md="3" className='d-flex align-items-center pt-3'>
                  <Form.Check
                    checked={newObject.isHidden}
                    onChange={(e) => handleChange('isHidden', e.target.checked)}
                    type="checkbox"
                    label="Скрытый объект"
                  />
                </Form.Group>
                <Form.Group as={Col} md="2" className='d-flex align-items-center pt-3'>
                  <Form.Check
                    checked={newObject.isArchived}
                    onChange={(e) => {
                      if(!e.target.checked){
                        handleChange('endDate', null)
                      }
                      handleChange('isArchived', e.target.checked)
                    }}
                    type="checkbox"
                    label="Архив"
                  />
                </Form.Group>
              </Row>
              <UserGroupEditor
                data={newObject && newObject.users
                  ? newObject.users
                  : []}
                onChange={(users) => handleChange('users', users)}
                roles={roles}
              />
            </Card.Body>
          </Card>
          <Card>
            <Card.Body className="p-4">
              <Row>
                <Form.Group as={Col}>
                  <Button className="me-2" variant="outline-secondary" onClick={() => history.goBack()}>Отмена</Button>
                  <Button className="me-2" variant="primary" type="submit">Сохранить</Button>
                </Form.Group>
              </Row>
            </Card.Body>
          </Card>
        </Form>
      </div>
    </section>
  );
}
